import boundingBoxHelperFactory from '../bounding-box-helper';
import colorHelper from '../colors-helper';
import geometryHelper from '../geometry-helper';
import { Matrix4, Vector3 } from '../../shim/three';
import { transformVertices } from '../matrix-helper';
import { getStencilData } from '../venue-stencil-helper';
import {
    DEFAULT_RENDER_OPTIONS,
    DEFAULT_STROKE,
    FONT_COLOR_LIGHT,
    FONT_THUMBNAILS_DARK_COLOR,
    LINE_THICKNESS,
    RENDER_OPTION_SEAT_LABELS,
    STANDARD_LABELS,
    STENCIL_COLOR,
} from '../../constants/render-constants';

const { isDark } = colorHelper;

const _fontColorDark = FONT_THUMBNAILS_DARK_COLOR;
const _fontColorLight = FONT_COLOR_LIGHT;

const MARGIN = 0.08;
const COLOR = '#FF00C8';
const MIN_CHAIR_RADIUS_IN_DISPLAY_COORDINATES = 1.2;

const { getCenterFromBoundingBox } = geometryHelper;

const genericMapRendererFactory = ({ renderer }) => {
    const {
        createRenderSurface,
        drawLine,
        drawOval,
        drawRectangle,
        drawShape,
        drawTexturedRectangle,
        drawText,
        getChairRadius,
        getCircleShape,
        getImageData,
        getSeatLabelsSize,
        getSectionLabelsSize,
        getTableColor,
        getColorFromIdAndTier,
    } = renderer;

    const _calculateChildMatrix = (properties, parentMatrix) => {
        const { angle, x, y } = properties;

        if (x !== undefined && y !== undefined) {
            let theta = angle;

            if (theta) {
                theta = (theta * Math.PI) / 180;
            }
            const objMatrix = new Matrix4();

            objMatrix.makeRotationZ(theta || 0);
            objMatrix.setPosition(new Vector3(x, y, 0));
            return parentMatrix.clone().multiply(objMatrix);
        }
        return parentMatrix;
    };

    const _getStatusDictionary = (statusNode) => {
        if (!statusNode) {
            return {};
        }
        const statusDictionary = {};

        Object.keys(statusNode).forEach((childName) => {
            const container = statusNode[childName] || {};
            const s = container.s || [];
            const S = container.S;

            if (S !== undefined) {
                statusDictionary[childName] = {
                    S,
                };
            }
            if (s.length) {
                for (let i = 0; i < s.length; i++) {
                    const id = `${childName}:${i + 1}`;

                    statusDictionary[id] = {
                        s: s[i],
                    };
                }
            }
        });
        return statusDictionary;
    };

    const _renderLineStencil = ({
        properties,
        renderList,
        modelViewMatrix,
    }) => {
        const { x, x2, y, y2 } = properties;
        const { lines } = renderList;

        const transformedVertices = transformVertices(
            [0, 0, 0, +x2 - x, +y2 - y, 0],
            modelViewMatrix,
        );

        const [xt, yt, , x2t, y2t, ,] = transformedVertices;

        return [
            ...lines,
            {
                color: DEFAULT_STROKE,
                thickness: LINE_THICKNESS,
                x: xt,
                x2: x2t,
                y: yt,
                y2: y2t,
            },
        ];
    };

    const _calculateShapeProperties = ({
        x,
        y,
        widthToUse,
        heightToUse,
        modelViewMatrix,
    }) => {
        // We add the vertices of the rectangle
        const vertices = [
            x - widthToUse * 0.5,
            y,
            0,
            x + widthToUse * 0.5,
            y,
            0,
            x,
            y - heightToUse * 0.5,
            0,
            x,
            y + heightToUse * 0.5,
            0,
            x,
            y,
            0,
        ];

        const transformedVertices = transformVertices(
            vertices,
            modelViewMatrix,
        );
        const pLeft = new Vector3(
            transformedVertices[0],
            transformedVertices[1],
            transformedVertices[2],
        );
        const pRight = new Vector3(
            transformedVertices[3],
            transformedVertices[4],
            transformedVertices[5],
        );
        const pTop = new Vector3(
            transformedVertices[6],
            transformedVertices[7],
            transformedVertices[8],
        );
        const pBottom = new Vector3(
            transformedVertices[9],
            transformedVertices[10],
            transformedVertices[11],
        );

        const cx = transformedVertices[12];
        const cy = transformedVertices[13];
        const rectWidth = pLeft.distanceTo(pRight);
        const rectHeight = pTop.distanceTo(pBottom);
        const theta =
            (Math.atan2(pRight.y - pLeft.y, pRight.x - pLeft.x) * 180) /
            Math.PI;

        return {
            cx,
            cy,
            rectWidth,
            rectHeight,
            theta,
        };
    };

    const _addShapeLabel = ({
        color,
        cx,
        cy,
        modelViewMatrix,
        text,
        texts,
        theta,
    }) => {
        const labelBaseSize = getSectionLabelsSize(modelViewMatrix);

        return [
            ...texts,
            {
                cx,
                cy,
                height: labelBaseSize,
                text,
                theta,
                color,
                width: labelBaseSize,
            },
        ];
    };

    const _renderShape = ({
        properties,
        modelViewMatrix,
        venueStencils,
        renderList,
        tierDictionary,
        statusDictionary,
        renderOptions,
    }) => {
        const {
            height,
            id,
            label,
            shape,
            stencil,
            sx = 0,
            sy = 0,
            t: tier,
            table_h: tableH,
            table_r: tableR,
            table_w: tableW,
            type,
            width,
            l: shapeLabel,
        } = properties;

        const { ovals, rectangles, texturedRectangles } = renderList;
        let { texts, lines } = renderList;

        let widthToUse = width;
        let heightToUse = height;

        if (tableR) {
            widthToUse = 2 * tableR;
            heightToUse = 2 * tableR;
        }
        if (tableH && tableW) {
            widthToUse = tableW;
            heightToUse = tableH;
        }
        let cx = 0;
        let cy = 0;
        let rectWidth = 0;
        let rectHeight = 0;
        let theta = 0;

        if (widthToUse !== undefined && heightToUse !== undefined) {
            ({ cx, cy, rectWidth, rectHeight, theta } =
                _calculateShapeProperties({
                    x: sx,
                    y: sy,
                    widthToUse,
                    heightToUse,
                    modelViewMatrix,
                }));
        }

        switch (type) {
            case 'venue_stencil':
                {
                    const venueStencilId = venueStencils[stencil];
                    const stencilRecord = getStencilData(venueStencilId);

                    texturedRectangles.push({
                        cx,
                        cy,
                        rectHeight,
                        rectWidth,
                        stencil: stencilRecord,
                        theta,
                        z: 0,
                    });
                }

                break;
            case 'tableAndChair':
                {
                    const tableType = properties.tableType;

                    if (tableType === 'circle') {
                        ovals.push({
                            color: getTableColor(),
                            cx,
                            cy,
                            rectHeight,
                            rectWidth,
                            theta,
                        });
                    } else {
                        rectangles.push({
                            color: getTableColor(),
                            cx,
                            cy,
                            rectHeight,
                            rectWidth,
                            theta,
                        });
                    }
                    if (renderOptions[RENDER_OPTION_SEAT_LABELS]) {
                        texts = _addShapeLabel({
                            color: _fontColorDark,
                            cx,
                            cy,
                            modelViewMatrix,
                            text: shapeLabel,
                            texts,
                            theta,
                        });
                    }
                }
                break;
            case 'line_stencil':
                lines = _renderLineStencil({
                    properties,
                    renderList,
                    modelViewMatrix,
                });
                break;
            case 'general_admission_section':
            case 'stencil':
                {
                    const isBackgroundDark = isDark(STENCIL_COLOR);
                    let colorToUse = STENCIL_COLOR;

                    if (type === 'general_admission_section') {
                        colorToUse = getColorFromIdAndTier({
                            id,
                            tierDictionary,
                            tierNumber: tier,
                            statusDictionary,
                            renderOptions,
                            defaultTierColor: COLOR,
                        });
                        if (renderOptions[RENDER_OPTION_SEAT_LABELS]) {
                            texts = _addShapeLabel({
                                color: _fontColorDark,
                                cx,
                                cy,
                                modelViewMatrix,
                                text: shapeLabel,
                                texts,
                                theta,
                            });
                        }
                    }

                    if (shape === 'circle') {
                        ovals.push({
                            color: colorToUse,
                            cx,
                            cy,
                            rectHeight,
                            rectWidth,
                            theta,
                        });
                    } else {
                        rectangles.push({
                            color: colorToUse,
                            cx,
                            cy,
                            rectHeight,
                            rectWidth,
                            theta,
                        });
                    }

                    if (stencil) {
                        const labelToUse = label || STANDARD_LABELS[stencil];
                        const fontColor = isBackgroundDark
                            ? _fontColorLight
                            : _fontColorDark;

                        // Stage label is always rendered
                        if (stencil === 'stage') {
                            // We draw the stage with font size 1 (size does not matter),
                            // as the renderer will take care of fitting it to the rectangle area.
                            texts = [
                                ...texts,
                                {
                                    cx,
                                    cy,
                                    height: rectHeight,
                                    text: labelToUse,
                                    theta,
                                    color: fontColor,
                                    width: rectWidth,
                                },
                            ];
                        } else {
                            if (renderOptions[RENDER_OPTION_SEAT_LABELS]) {
                                texts = _addShapeLabel({
                                    color: fontColor,
                                    cx,
                                    cy,
                                    modelViewMatrix,
                                    text: labelToUse,
                                    texts,
                                    theta,
                                });
                            }
                        }
                    }
                }
                break;
            case 'row':
            case 'section':
                if (renderOptions[RENDER_OPTION_SEAT_LABELS]) {
                    let labelX;
                    let labelY;
                    const labelBaseSize = getSectionLabelsSize(modelViewMatrix);

                    if (type === 'row') {
                        ({ label1_X: labelX, label1_Y: labelY } = properties);
                    } else {
                        ({ label_x: labelX, label_y: labelY } = properties);
                    }

                    ({ cx, cy, theta } = _calculateShapeProperties({
                        x: labelX,
                        y: labelY,
                        widthToUse: 0,
                        heightToUse: 0,
                        modelViewMatrix,
                    }));

                    texts = [
                        ...texts,
                        {
                            cx,
                            cy,
                            height: labelBaseSize,
                            text: shapeLabel,
                            theta,
                            color: _fontColorDark,
                            width: labelBaseSize,
                        },
                    ];
                }

                break;
            default:
                break;
        }

        return {
            modelViewMatrix,
            venueStencils,
            renderList: {
                ...renderList,
                ovals,
                rectangles,
                texturedRectangles,
                texts,
                lines,
            },
            tierDictionary,
            statusDictionary,
        };
    };

    const _renderChairs = ({
        chairs,
        modelViewMatrix,
        renderSurface,
        renderList,
        tierDictionary,
        statusDictionary,
        renderOptions,
    }) => {
        const { d, id, l, t, x, y } = chairs;
        const vertices = [];
        const { circleCenters, circleColors, circleShapes } = renderList;
        let { texts } = renderList;
        const _isChairDeleted = (chair) => !chair;

        for (let i = 0; i < x.length; i++) {
            vertices.push(x[i], y[i], 0);
            const tierNumber = t[i];

            if (_isChairDeleted(d[i])) {
                circleColors.push(
                    getColorFromIdAndTier({
                        id: id[i],
                        tierDictionary,
                        tierNumber,
                        statusDictionary,
                        renderOptions,
                        defaultTierColor: COLOR,
                    }),
                );
            }
        }

        const transformedVertices = transformVertices(
            vertices,
            modelViewMatrix,
        );
        let chairRadius = getChairRadius(modelViewMatrix);

        if (chairRadius < MIN_CHAIR_RADIUS_IN_DISPLAY_COORDINATES) {
            chairRadius = MIN_CHAIR_RADIUS_IN_DISPLAY_COORDINATES;
        }
        const chairShape = getCircleShape(renderSurface, chairRadius);

        for (let i = 0; i < x.length; i++) {
            if (_isChairDeleted(d[i])) {
                const baseIndex = i * 3;

                const x = transformedVertices[baseIndex];
                const y = transformedVertices[baseIndex + 1];
                circleCenters.push(x, y);
                circleShapes.push(chairShape);

                if (renderOptions[RENDER_OPTION_SEAT_LABELS]) {
                    const labelBaseSize = getSeatLabelsSize(modelViewMatrix);
                    texts = [
                        ...texts,
                        {
                            cx: x,
                            cy: y,
                            height: labelBaseSize,
                            text: l[i],
                            color: _fontColorDark,
                            width: labelBaseSize,
                        },
                    ];
                }
            }
        }

        return {
            renderSurface,
            renderList: {
                ...renderList,
                circleCenters,
                circleColors,
                circleShapes,
                texts,
            },
            tierDictionary,
            statusDictionary,
        };
    };

    const _render = ({
        node,
        parentMatrix,
        renderList,
        renderSurface,
        statusDictionary,
        tierDictionary,
        venueStencils,
        viewMatrix,
        renderOptions,
    }) => {
        let matrixToUse = parentMatrix || new Matrix4();

        const { properties, chairs, contents } = node;

        if (properties) {
            matrixToUse = _calculateChildMatrix(properties, matrixToUse);
        }

        let modelViewMatrix = viewMatrix.clone().multiply(matrixToUse);
        let updatedVenueStencils,
            updatedRenderList,
            updatedTierDictionary,
            updatedStatusDictionary,
            updatedRenderSurface;

        ({
            modelViewMatrix,
            venueStencils: updatedVenueStencils,
            renderList: updatedRenderList,
            tierDictionary: updatedTierDictionary,
            statusDictionary: updatedStatusDictionary,
        } = _renderShape({
            properties,
            modelViewMatrix,
            venueStencils,
            renderList,
            tierDictionary,
            statusDictionary,
            renderOptions,
        }));

        if (chairs) {
            ({
                renderSurface: updatedRenderSurface,
                renderList: updatedRenderList,
                tierDictionary: updatedTierDictionary,
                statusDictionary: updatedStatusDictionary,
            } = _renderChairs({
                chairs,
                modelViewMatrix,
                renderSurface,
                renderList: updatedRenderList,
                tierDictionary: updatedTierDictionary,
                statusDictionary: updatedStatusDictionary,
                renderOptions,
            }));
        }

        if (contents) {
            Object.keys(contents).forEach((childName) => {
                const child = contents[childName];

                ({
                    renderList: updatedRenderList,
                    renderSurface: updatedRenderSurface,
                } = _render({
                    node: child,
                    parentMatrix: matrixToUse,
                    renderList: updatedRenderList,
                    renderSurface: updatedRenderSurface || renderSurface,
                    statusDictionary: updatedStatusDictionary,
                    tierDictionary: updatedTierDictionary,
                    venueStencils: updatedVenueStencils,
                    viewMatrix,
                    renderOptions,
                }));
            });
        }

        return {
            renderList: updatedRenderList,
            renderSurface: updatedRenderSurface || renderSurface,
        };
    };

    const _getViewMatrixFromBoundingBox = ({
        boundingBox,
        margin,
        viewport,
    }) => {
        const { height: viewportHeight, width: viewportWidth } = viewport;
        const { max, min } = boundingBox;
        const worldWidth = (max.x - min.x) * (1 + margin);
        const worldHeight = (max.y - min.y) * (1 + margin);
        const fW = viewportWidth / worldWidth;
        const fH = viewportHeight / worldHeight;
        const f = fW < fH ? fW : fH;
        const worldCenter = getCenterFromBoundingBox(boundingBox);

        const moveWorldToCenter = new Matrix4().setPosition({
            x: -worldCenter.x,
            y: -worldCenter.y,
            z: 0,
        });
        const scaleWorld = new Matrix4().scale({ x: f, y: f, z: 1 });
        const moveScaledToViewportCenter = new Matrix4().setPosition({
            x: 0.5 * viewportWidth,
            y: 0.5 * viewportHeight,
            z: 0,
        });
        const viewMatrix = moveScaledToViewportCenter.multiply(
            scaleWorld.multiply(moveWorldToCenter),
        );

        return viewMatrix;
    };

    const getViewMatrix = ({ node, margin = MARGIN, viewport }) => {
        const boundingBoxHelper = boundingBoxHelperFactory();
        const boundingBox = boundingBoxHelper.getBoundingBox({ node });
        const viewMatrix = _getViewMatrixFromBoundingBox({
            boundingBox,
            margin,
            viewport,
        });

        return viewMatrix;
    };

    const _getViewBoxFromViewMatrixAndViewPort = ({ viewMatrix, viewport }) => {
        const { height: viewportHeight, width: viewportWidth } = viewport;
        const viewMatrixInv = viewMatrix.getInverse(viewMatrix);
        const zeroCorner = new Vector3(0, 0, 0);
        const maxCorner = new Vector3(viewportWidth, viewportHeight, 0);
        const zeroTransformed = zeroCorner.applyMatrix4(viewMatrixInv);
        const maxTransformed = maxCorner.applyMatrix4(viewMatrixInv);
        const cx = (zeroTransformed.x + maxTransformed.x) * 0.5;
        const cy = (zeroTransformed.y + maxTransformed.y) * 0.5;
        const viewBoxWidth = Math.abs(zeroTransformed.x - maxTransformed.x);
        const viewBoxHeight = Math.abs(zeroTransformed.y - maxTransformed.y);

        return {
            cx,
            cy,
            width: viewBoxWidth,
            height: viewBoxHeight,
        };
    };

    const getViewBox = ({ node = {}, margin, viewport }) => {
        const viewMatrix = getViewMatrix({ node, margin, viewport });
        const viewBox = _getViewBoxFromViewMatrixAndViewPort({
            viewMatrix,
            viewport,
        });

        return viewBox;
    };

    const getThumbnail = async ({
        margin,
        node = {},
        viewport,
        renderOptions = DEFAULT_RENDER_OPTIONS,
    }) => {
        const { height, subsampling, width } = viewport;

        const viewMatrix = getViewMatrix({
            node,
            margin,
            viewport,
        });

        const { objects, tiers: tierDictionary, status: statusNode } = node;
        const statusDictionary = _getStatusDictionary(statusNode);

        const venueStencils = node['venue_stencils'];
        let renderSurface = createRenderSurface({
            height,
            subsampling,
            width,
        });

        let rectangles = [];
        let ovals = [];
        let texturedRectangles = [];
        let circleCenters = [];
        let circleShapes = [];
        let circleColors = [];
        let lines = [];
        let texts = [];

        const renderList = {
            circleCenters,
            circleColors,
            circleShapes,
            lines,
            ovals,
            rectangles,
            texts,
            texturedRectangles,
        };

        ({
            renderList: {
                rectangles,
                ovals,
                texturedRectangles,
                circleCenters,
                circleShapes,
                circleColors,
                lines,
                texts,
            },
            renderSurface,
        } = _render({
            node: objects,
            parentMatrix: null,
            renderList,
            renderSurface,
            statusDictionary,
            tierDictionary,
            venueStencils,
            viewMatrix,
            renderOptions,
        }));

        for (let i = 0; i < texturedRectangles.length; i++) {
            const texturedRectangle = texturedRectangles[i];
            const { cx, cy, rectHeight, rectWidth, stencil, theta } =
                texturedRectangle;

            drawTexturedRectangle(
                renderSurface,
                cx,
                cy,
                rectWidth,
                rectHeight,
                theta,
                stencil,
            );
        }
        for (let i = 0; i < rectangles.length; i++) {
            const rectangle = rectangles[i];
            const { color, cx, cy, rectHeight, rectWidth, theta } = rectangle;

            drawRectangle(
                renderSurface,
                cx,
                cy,
                rectWidth,
                rectHeight,
                theta,
                color,
            );
        }
        for (let i = 0; i < ovals.length; i++) {
            const oval = ovals[i];
            const { color, cx, cy, rectHeight, rectWidth, theta } = oval;

            drawOval(
                renderSurface,
                cx,
                cy,
                rectWidth,
                rectHeight,
                theta,
                color,
            );
        }
        for (let i = 0; i < circleShapes.length; i++) {
            const cx = circleCenters[i << 1];
            const cy = circleCenters[(i << 1) + 1];
            const shape = circleShapes[i];
            const color = circleColors[i];

            drawShape(renderSurface, cx, cy, shape, color);
        }

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const { x, y, x2, y2, thickness, color } = line;

            drawLine(renderSurface, x, y, x2, y2, thickness, color);
        }

        for (let i = 0; i < texts.length; i++) {
            const text = texts[i];

            drawText(renderSurface, text);
        }

        const imageData = getImageData(renderSurface);

        return imageData;
    };

    return {
        getThumbnail,
        getViewBox,
        getViewMatrix,
    };
};

export default genericMapRendererFactory;
